import { Formik, FormikProps } from 'formik';
import React, { forwardRef } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FormFieldType } from '../../../enums';
import { ShippingAddressFormData } from '../../../interfaces';

import { useShippingAddressFormValidation } from './shippingAddressForm.hooks';
import { FieldsRow, FormikForm, StyledFormField } from './shippingAddressForm.styled';
import { ShippingAddressFormProps } from './shippingAddressForm.types';

export const ShippingAddressForm: React.FC<ShippingAddressFormProps> = forwardRef((
  props: ShippingAddressFormProps, ref: React.Ref<FormikProps<ShippingAddressFormData>>
): JSX.Element => {
  const { initialValues, isFormSubmitted, className }: ShippingAddressFormProps = props;
  const shippingAddressFormValidationSchema: Yup.SchemaOf<ShippingAddressFormData> = useShippingAddressFormValidation();
  const { t }: TransProps<never> = useTranslation();

  return (
    <Formik
      className={className}
      initialValues={{ ...initialValues, country: 'Polska' }}
      onSubmit={(): void => undefined}
      innerRef={ref}
      validationSchema={shippingAddressFormValidationSchema}
      validateOnChange={isFormSubmitted}
      validateOnBlur={isFormSubmitted}
      enableReinitialize
    >
      {({ handleSubmit, setFieldValue, errors, values }: FormikProps<ShippingAddressFormData>): JSX.Element => (
        <FormikForm onSubmit={handleSubmit}>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.firstName.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.firstName}
              fieldName='firstName'
              validationMessage={isFormSubmitted ? errors.firstName : undefined}
            />
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.lastName.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.lastName}
              fieldName='lastName'
              validationMessage={isFormSubmitted ? errors.lastName : undefined}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.phone.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.phone}
              fieldName='phone'
              validationMessage={isFormSubmitted ? errors.phone : undefined}
              withfullWidthFieldContainer
              additionalFieldProps={{
                withOnlyNumbersAllowed: true
              }}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.street.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.street}
              fieldName='street'
              validationMessage={isFormSubmitted ? errors.street : undefined}
              withfullWidthFieldContainer
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.houseNumber.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.houseNumber}
              fieldName='houseNumber'
              validationMessage={isFormSubmitted ? errors.houseNumber : undefined}
            />
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.apartmentNumber.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.apartmentNumber ?? ''}
              fieldName='apartmentNumber'
              validationMessage={isFormSubmitted ? errors.apartmentNumber : undefined}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.postalCode.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.postalCode}
              fieldName='postalCode'
              validationMessage={isFormSubmitted ? errors.postalCode : undefined}
              additionalFieldProps={{
                format: '##-###'
              }}
            />
            <StyledFormField
              label={t('onarte.ui.shippingAddressForm.city.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.city}
              fieldName='city'
              validationMessage={isFormSubmitted ? errors.city : undefined}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              disabled
              label={t('onarte.ui.shippingAddressForm.country.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.country}
              fieldName='country'
              withfullWidthFieldContainer
            />
          </FieldsRow>
        </FormikForm>
      )}
    </Formik>
  );
});
